import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { LoadingState, ErrorState } from "@/components/States";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, Save, Bot } from "lucide-react";

const inputCls = "bg-slate-900 border-slate-800 text-slate-100 focus-visible:ring-violet-500/30";

function RoleCard({ role, providers, onSaved }) {
  const [draft, setDraft] = useState({
    instructions: role.instructions, provider: role.provider, model: role.model, budget_cap: role.budget_cap,
  });
  const [saving, setSaving] = useState(false);

  const activeProvider = providers.find((p) => p.id === draft.provider) || providers[0];
  const modelUnavailable = activeProvider && !activeProvider.available;

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/ai-team/roles/${role.id}`, {
        instructions: draft.instructions, provider: draft.provider,
        model: draft.model, budget_cap: Number(draft.budget_cap),
      });
      toast.success(`${role.name} saved`);
      onSaved?.(data);
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="bg-[#111827] border border-slate-800/80 rounded-xl p-5 flex flex-col" data-testid={`role-card-${role.key}`}>
      <div className="flex items-center gap-2.5 mb-3">
        <div className="w-9 h-9 rounded-lg bg-violet-500/10 border border-violet-500/20 flex items-center justify-center flex-none">
          <Bot className="w-4 h-4 text-violet-300" />
        </div>
        <h3 className="font-display font-semibold text-slate-100 text-sm leading-tight">{role.name}</h3>
      </div>
      <p className="text-xs text-slate-500 mb-4">{role.purpose}</p>

      <Label className="text-slate-400 text-xs mb-1 block">Instructions</Label>
      <Textarea className={`${inputCls} text-xs min-h-[90px] mb-3`} value={draft.instructions}
        onChange={(e) => setDraft({ ...draft, instructions: e.target.value })} data-testid={`role-instructions-${role.key}`} />

      <div className="grid grid-cols-2 gap-2 mb-3">
        <div>
          <Label className="text-slate-400 text-xs mb-1 block">Provider</Label>
          <Select value={draft.provider} onValueChange={(v) => setDraft({ ...draft, provider: v })}>
            <SelectTrigger className={`${inputCls} h-9 text-xs`} data-testid={`role-provider-${role.key}`}><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#111827] border-slate-700 text-slate-200">
              {providers.map((p) => <SelectItem key={p.id} value={p.id} disabled={!p.available}>{p.label}{!p.available && " (unavailable)"}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-slate-400 text-xs mb-1 block">Budget cap ($)</Label>
          <Input type="number" step="0.01" className={`${inputCls} h-9 text-xs`} value={draft.budget_cap}
            onChange={(e) => setDraft({ ...draft, budget_cap: e.target.value })} data-testid={`role-budget-${role.key}`} />
        </div>
      </div>

      <Label className="text-slate-400 text-xs mb-1 block">Model</Label>
      {modelUnavailable ? (
        <div className="text-xs text-amber-300/90 bg-amber-500/10 border border-amber-500/25 rounded-lg px-3 py-2 mb-3" data-testid={`role-model-hint-${role.key}`}>
          {activeProvider.hint || "This provider is not connected."}
        </div>
      ) : (
        <Select value={draft.model} onValueChange={(v) => setDraft({ ...draft, model: v })}>
          <SelectTrigger className={`${inputCls} h-9 text-xs mb-3`} data-testid={`role-model-${role.key}`}><SelectValue placeholder="Select model" /></SelectTrigger>
          <SelectContent className="bg-[#111827] border-slate-700 text-slate-200 max-h-64">
            {(activeProvider?.models || []).map((m) => <SelectItem key={m.id} value={m.id}>{m.label}</SelectItem>)}
          </SelectContent>
        </Select>
      )}

      <Button onClick={save} disabled={saving} size="sm" className="mt-auto bg-violet-600 hover:bg-violet-500 text-white" data-testid={`role-save-${role.key}`}>
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1.5" /> Save</>}
      </Button>

      <div className="mt-3 pt-3 border-t border-slate-800/70">
        <RecentRuns roleId={role.id} roleKey={role.key} />
      </div>
    </div>
  );
}

function RecentRuns({ roleId, roleKey }) {
  const [runs, setRuns] = useState(null);
  useEffect(() => {
    api.get(`/ai-team/roles/${roleId}/runs`).then((r) => setRuns(r.data)).catch(() => setRuns([]));
  }, [roleId]);
  if (runs === null) return <p className="text-[11px] text-slate-600">Loading runs…</p>;
  if (runs.length === 0) return <p className="text-[11px] text-slate-600" data-testid={`role-runs-${roleKey}`}>No runs yet — trigger a task from the Command Center.</p>;
  return (
    <div data-testid={`role-runs-${roleKey}`}>
      <p className="text-[11px] font-semibold text-slate-400 mb-1.5">Recent runs</p>
      <div className="space-y-1">
        {runs.slice(0, 4).map((r) => (
          <div key={r.id} className="flex items-center justify-between text-[11px] text-slate-500">
            <span className="text-slate-400">{r.tool}</span>
            <span>{r.status} · {new Date(r.created_at).toLocaleTimeString()}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function AITeam() {
  const [roles, setRoles] = useState(null);
  const [providers, setProviders] = useState([]);
  const [error, setError] = useState(null);

  const load = async () => {
    setError(null);
    try {
      const [r, o] = await Promise.all([api.get("/ai-team/roles"), api.get("/ai-team/model-options")]);
      setRoles(r.data);
      setProviders(o.data.providers);
    } catch (e) { setError(apiError(e)); }
  };
  useEffect(() => { load(); }, []);

  if (error) return <ErrorState message={error} onRetry={load} />;
  if (!roles) return <LoadingState label="Loading AI team…" />;

  return (
    <div className="animate-in-up">
      <div className="mb-8">
        <p className="text-[11px] font-semibold tracking-widest uppercase text-violet-400 mb-1">Workspace</p>
        <h1 className="text-3xl sm:text-4xl font-display font-extrabold tracking-tight text-slate-50">AI Team</h1>
        <p className="text-sm text-slate-400 mt-2">Twelve roles that will run your pipeline. Configure each role's model and budget. These are configs — no agents run yet.</p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {roles.map((role) => (
          <RoleCard key={role.id} role={role} providers={providers}
            onSaved={(updated) => setRoles((rs) => rs.map((x) => (x.id === updated.id ? updated : x)))} />
        ))}
      </div>
    </div>
  );
}
